import { createStore } from "zustand/vanilla";
import type { FileNode, OpenFile } from "./types";
import { createEntry as apiCreateEntry, deleteEntry as apiDeleteEntry, fetchFileContent, fetchTree, saveFileContent } from "./api";

type EditorState = {
  tree: FileNode[];
  openFiles: OpenFile[];
  activePath: string | null;
  explorerOpen: boolean;

  loadTree: () => Promise<void>;
  openFile: (path: string) => Promise<void>;
  closeFile: (path: string) => void;
  setActivePath: (path: string) => void;
  setContent: (path: string, content: string) => void;
  saveActiveFile: () => Promise<void>;
  createEntry: (path: string, type: "file" | "directory") => Promise<void>;
  deleteEntry: (path: string) => Promise<void>;
  setExplorerOpen: (open: boolean) => void;
};

export const store = createStore<EditorState>((set, get) => ({
  tree: [],
  openFiles: [],
  activePath: null,
  explorerOpen: false,

  loadTree: async () => {
    const tree = await fetchTree();
    set({ tree });
  },

  openFile: async (path) => {
    const existing = get().openFiles.find((f) => f.path === path);
    if (existing) {
      set({ activePath: path, explorerOpen: false });
      return;
    }

    const content = await fetchFileContent(path);
    set((state) => ({
      openFiles: [...state.openFiles, { path, content, dirty: false }],
      activePath: path,
      explorerOpen: false,
    }));
  },

  closeFile: (path) => {
    set((state) => {
      const openFiles = state.openFiles.filter((f) => f.path !== path);
      const wasActive = state.activePath === path;
      const activePath = wasActive ? (openFiles[openFiles.length - 1]?.path ?? null) : state.activePath;
      return { openFiles, activePath };
    });
  },

  setActivePath: (path) => set({ activePath: path }),

  setContent: (path, content) => {
    set((state) => ({
      openFiles: state.openFiles.map((f) => (f.path === path ? { ...f, content, dirty: true } : f)),
    }));
  },

  saveActiveFile: async () => {
    const { activePath, openFiles } = get();
    const file = openFiles.find((f) => f.path === activePath);
    if (!file) return;

    await saveFileContent(file.path, file.content);

    set((state) => ({
      openFiles: state.openFiles.map((f) => (f.path === file.path ? { ...f, dirty: false } : f)),
    }));
  },

  createEntry: async (path, type) => {
    const ok = await apiCreateEntry(path, type);
    if (ok) await get().loadTree();
  },

  deleteEntry: async (path) => {
    const ok = await apiDeleteEntry(path);
    if (ok) {
      await get().loadTree();
      get().closeFile(path);
    }
  },

  setExplorerOpen: (open) => set({ explorerOpen: open }),
}));
